import * as React from 'react';
import * as Styled from './styled';
import { NavBarStyles } from './styled';
import {
    Assignment,
    ChevronLeft,
    ChevronRight,
    Cloud,
    Dashboard,
    DateRange,
    FileCopy,
    Home,
    List as ListIcon,
    Note,
    Settings,
    Timer
} from '@material-ui/icons';
import { FlexDiv } from 'common/flex';
import {
    Badge,
    Divider,
    Drawer,
    ListItemIcon,
    Menu,
    MenuItem,
    Theme,
    Tooltip,
    withStyles
} from '@material-ui/core';
import { inject, observer } from 'mobx-react';
import { RootStore } from 'store/root.store';
import AppStore from 'store/app.store';
import { withRouter } from 'react-router';
import NavigationStore from 'store/navigation.store';
import { Platform } from '../../util/Platform';
import TimeKeepersList from 'components/TimeKeepersList/TimeKeepersList';
import { DateTime } from 'luxon';
import { FeaturesConsumer } from 'common/FeaturesProvider';
import { Features } from '../../api/types/types';
import { withTranslation } from 'react-i18next';
import CssBaseline from '@material-ui/core/CssBaseline';
import classNames from 'classnames';
import { MainNavigationHotkeys, Pages } from 'util/hotKeys';

interface State {
    open: boolean;
    userMenuOpen: boolean;
    managementMenuOpen: boolean;
    selectedPage: string;
}

// tslint:disable:no-any
interface Props {
    mainContainerRef: React.RefObject<HTMLDivElement>;
    appStore?: AppStore;
    routerStore?: NavigationStore;
    match: {
        path: string;
        params: string;
        isExact: boolean;
        url: string;
    } | any;
    location: {
        pathname: string
    } | any;
    history: any;
    classes?: any;
    t: any;
    theme: Theme;
}
// tslint:enable:no-any

function getInitials(name: string): string {
    const firstAndLastName = (name.trim() || '').split(/\s/);
    const firstName = firstAndLastName.shift() || '';
    const lastName = firstAndLastName.pop() || '';
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;

    return initials.toUpperCase();
}

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        appStore: rootStore.appStore,
        routerStore: rootStore.routerStore
    };
})
@observer
class NavBar extends React.Component<Props, State> {
    userMenuAnchor = React.createRef<HTMLLIElement>();
    managementMenuAnchor = React.createRef<HTMLLIElement>();
    selectedTabRef = React.createRef<HTMLLIElement>();
    preventClick: boolean = false;
    navigationHotkeys: { [key: string]: string };
    // tslint:disable-next-line:no-any
    timer: any;

    constructor(props: Props) {
        super(props)
        this.navigationHotkeys = this.getNavigationHotkeys();
        this.state = {
            open: false,
            userMenuOpen: false,
            managementMenuOpen: false,
            selectedPage: this.props.location.pathname.split('/')[1]
        };
    }
    componentDidMount() {
        document.addEventListener('keydown', this.handleHotKeyNavigation, false);
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleHotKeyNavigation);
    }

    getNavigationHotkeys = () => {
        const {
            EpochConfigMattersPageEnabled,
            EpochConfigTimersEnabled,
            EpochConfigReportsEnabled,
            EpochConfigManagementDashboard
        } = this.props.appStore!.features;

        const navigationHotkeys = { ...MainNavigationHotkeys };
        Object.entries(MainNavigationHotkeys).forEach(([key, url]) => {
            if ((!EpochConfigMattersPageEnabled && url === Pages.MATTERS) ||
                (!EpochConfigTimersEnabled && url === Pages.TIMERS) ||
                (!EpochConfigManagementDashboard && (url === Pages.PROJECT_TIME || url === Pages.DRAFT_TIME)) ||
                (!EpochConfigReportsEnabled && url === Pages.REPORTS)
            ) {
                delete navigationHotkeys[key];
            }
        });
        return navigationHotkeys;
    }

    handleHotKeyNavigation = (e: KeyboardEvent) => {
        const { appStore, mainContainerRef, location } = this.props;
        const page = MainNavigationHotkeys[e.key];
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && page) { // TODO: use event.metaKey for mac and not windows.
            e.stopPropagation();
            e.preventDefault();
            const pageEnabled = this.navigationHotkeys[e.key];
            if (!appStore!.someDialogOpen && pageEnabled) {
                this.goToView(page);
            }
        }
        if (e.key === 'Escape' && !e.shiftKey && !e.ctrlKey && !appStore!.dialogClosing) {
            if (appStore!.previousActiveElements.length > 0) {
                appStore!.focusPreviousActiveElement();
            } else if (mainContainerRef.current && mainContainerRef.current.contains(document.activeElement)) {
                e.stopPropagation();
                e.preventDefault();
                if (this.selectedTabRef.current) {
                    this.selectedTabRef.current.focus();
                } else if (location.pathname.startsWith('/management') && this.managementMenuAnchor.current) {
                    this.managementMenuAnchor.current.focus();
                }
            }
        }
    }

    handleDrawerOpen = () => {
        this.setState({ open: true });
    }

    handleDrawerClose = () => {
        this.setState({ open: false });
    }

    synchronize = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        clearTimeout(this.timer);
        this.preventClick = true;
        if (e.shiftKey) {
            if (Platform.isElectron()) {
                this.props.appStore!.reSynchronize();
            } else {
                this.props.appStore!.getFeaturesApi();
            }
        } else {
            this.props.appStore!.synchronize();
        }
    }

    handleTkSearch = (evt: React.ChangeEvent<HTMLInputElement>) => {
        this.props.appStore!.setTkSearchText(evt.target.value);
    }

    takeMeToOfflineView = () => {
        if (this.props.appStore!.offlineEntryCount === 0) {
            return;
        }
        if (Platform.isElectron()) {
            this.props.routerStore!.attemptPush(`/settings/offlineEntries`);
        }
    }

    goToOfflineView = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        this.timer = setTimeout(() => {
            if (!this.preventClick) {
                this.takeMeToOfflineView();
            }
            this.preventClick = false;
        }, 500);
    }

    goToView = async (path: string) => {
        this.props.appStore!.resetPreviousActiveElements();
        const selectedPage = path.split('/')[1];
        if (this.state.selectedPage !== selectedPage || (selectedPage === 'management' && this.props.location.pathname !== path)) {
            this.props.appStore!.rootStore.managementDashboardStore.getManagmentRoles();
            if (await this.props.routerStore!.attemptPush(path)) {
                this.setState({ selectedPage });
            }
        }
        const el = document.querySelector('[data-auto-focus="true"]') as HTMLElement;
        if (el) {
            el.focus();
        }
    }

    logout = () => {
        this.setState({ userMenuOpen: false });
        if (Platform.isElectron()) {
            this.props.appStore!.attemptHardLogout();
        } else {
            this.props.appStore!.logOut();
        }
    }
    customListItem = (titleKey: string, page?: string, icon?: React.ReactElement, component?: React.ReactElement) => {
        const selected = page ? page.split('/')[1] === this.state.selectedPage : undefined;
        return (
            <Tooltip
                title={titleKey}
                placement="right"
                disableHoverListener={this.state.open}
                disableFocusListener={this.state.open}
                disableTouchListener={this.state.open}
            >
                {page && icon ?
                    <Styled.NavItem
                        id={page.split('/')[1]}
                        button={true}
                        buttonRef={selected ? this.selectedTabRef : undefined}
                        selected={selected}
                        onClick={() => this.goToView(page)}
                        style={{
                            backgroundColor: 'transparent'
                        }}
                    >
                        <ListItemIcon>{icon}</ListItemIcon>
                        <Styled.NavItemText primary={titleKey}/>
                    </Styled.NavItem>
                :
                    <>{component ? component : ''}</>
                }
            </Tooltip>
        );
    }

    render() {
        const { open } = this.state;
        const { classes, t, theme, appStore } = this.props;
        const {
            user,
            currentTimekeeper,
            uniqueTimeKeepers,
            online,
            offlineEntryCount,
            filteredTimeKeepers,
            disableAnimations,
            rootStore
        } = appStore!;

        // @ts-ignore
        return (
            <FeaturesConsumer>
                {(features: Features) =>
                    <>
                        {this.props.location.pathname.startsWith(`/timersPopOut`) ? '' :
                            <>
                                <CssBaseline />
                                <Drawer
                                    variant="permanent"
                                    className={classNames(classes.drawer, {
                                        [classes.drawerOpen]: this.state.open,
                                        [classes.drawerClose]: !this.state.open,
                                    })}
                                    classes={{
                                        paper: classNames({
                                            [classes.drawerOpen]: this.state.open,
                                            [classes.drawerClose]: !this.state.open,
                                        }),
                                    }}
                                    open={this.state.open}
                                    PaperProps={{
                                        style: {
                                            backgroundColor: theme.palette.secondary.main,
                                            overflow: 'hidden'
                                        }
                                    }}
                                >
                                    <Divider />
                                    <Styled.IconList
                                        expanded={open} 
                                        animationsEnabled={!disableAnimations}
                                    >
                                        <Styled.Logo onClick={open ? this.handleDrawerClose : this.handleDrawerOpen} aria-hidden="true">
                                            <Styled.EPImg src="static/epoch-logo-text-left.png"/>
                                            <Styled.OImg src="static/epoch-logo-small.png"/>
                                            <Styled.CHImg src="static/epoch-logo-text-right.png"/>
                                        </Styled.Logo>
                                        {this.customListItem(t('app.nav_bar.home'), '/home', <Home className={classes.icon}/>)}
                                        {this.customListItem(t('app.nav_bar.timeentries'), '/timeentries', <DateRange className={classes.icon}/>)}
                                        {features.EpochConfigMattersPageEnabled &&
                                            this.customListItem(
                                                t('app.nav_bar.matters', {matterLabel: features.EpochConfigMatterLabel}),
                                                '/matters',
                                                <ListIcon className={classes.icon}/>
                                            )
                                        }
                                        {this.customListItem(t('app.nav_bar.templates'), '/templates', <FileCopy className={classes.icon}/>)}
                                        {features.EpochConfigTimersEnabled &&
                                            this.customListItem(t('app.nav_bar.timers'), '/timers', <Timer className={classes.icon}/>)
                                        }
                                        {this.customListItem(t('app.nav_bar.narratives'), '/narratives', <Note className={classes.icon}/>)}
                                        {features.EpochConfigManagementDashboard && rootStore.managementDashboardStore.roles.length > 0 &&
                                            <>
                                                {this.customListItem(t('app.nav_bar.management.title'), undefined, undefined,
                                                    <Styled.NavItem
                                                        id="management"
                                                        button={true}
                                                        selected={this.props.location.pathname.startsWith('/management') || this.state.managementMenuOpen}
                                                        onClick={() => this.setState({managementMenuOpen: true})}
                                                        buttonRef={this.managementMenuAnchor}
                                                        style={{
                                                            backgroundColor: 'transparent'
                                                        }}
                                                    >
                                                        <ListItemIcon><Assignment className={classes.icon}/></ListItemIcon>
                                                        <Styled.NavItemText primary={t('app.nav_bar.management.title')}/>
                                                    </Styled.NavItem>
                                                )}
                                                <Menu
                                                    anchorOrigin={{horizontal: 'center', vertical: 'center'}}
                                                    anchorEl={this.managementMenuAnchor.current}
                                                    open={this.state.managementMenuOpen}
                                                    onClose={() => this.setState({managementMenuOpen: false})}
                                                >
                                                    <MenuItem
                                                        selected={this.props.location.pathname.startsWith('/management/project-time')}
                                                        onClick={() => {
                                                            this.setState({managementMenuOpen: false}, () => {
                                                                this.goToView('/management/project-time');
                                                            });
                                                        }}
                                                    >
                                                        {t('app.nav_bar.management.menu.project_time')}
                                                    </MenuItem>
                                                    <MenuItem
                                                        selected={this.props.location.pathname.startsWith('/management/draft-time')}
                                                        onClick={() => {
                                                            this.setState({managementMenuOpen: false}, () => {
                                                                this.goToView('/management/draft-time');
                                                            });
                                                        }}
                                                    >
                                                        {t('app.nav_bar.management.menu.draft_time')}
                                                    </MenuItem>
                                                </Menu>
                                            </>
                                        }
                                        {this.customListItem(t('app.nav_bar.settings'), '/settings/general', <Settings className={classes.icon}/>)}
                                        {features.EpochConfigReportsEnabled &&
                                            this.customListItem(t('app.nav_bar.reports'), '/reports', <Dashboard className={classes.icon}/>)
                                        }
                                        <FlexDiv flex={1}/>
                                        {this.customListItem(t(`app.nav_bar.cloud.status.${online ? 'connected' : 'offline'}`), undefined, undefined,
                                            <Tooltip title={t(`app.nav_bar.cloud.status.${online ? 'connected' : 'offline'}`)}>
                                                <Styled.NavItem button={true}>
                                                    <ListItemIcon aria-hidden="true">
                                                        <Badge badgeContent={offlineEntryCount} color="error">
                                                            <Cloud
                                                                onClick={this.goToOfflineView}
                                                                onDoubleClick={this.synchronize}
                                                                className={classes.icon}
                                                                style={{
                                                                    color: online ? theme.palette.common.white : theme.palette.offline.main
                                                                }}
                                                            />
                                                        </Badge>
                                                    </ListItemIcon>
                                                    <Styled.NavItemText primary={t(`app.nav_bar.cloud.status.${online ? 'connected' : 'offline'}`)}/>
                                                </Styled.NavItem>
                                            </Tooltip>
                                        )}
                                        <TimeKeepersList
                                            render={(tkMenuAnchor, openTkList) => (
                                                <div ref={tkMenuAnchor as React.LegacyRef<HTMLDivElement>}>
                                                    <Styled.NavItem
                                                        button={true}
                                                        onClick={openTkList}
                                                        style={{overflow: 'visible', cursor: 'pointer'}}
                                                        aria-label={`${t('app.nav_bar.timekeeper')} ${currentTimekeeper.name}`}
                                                    >
                                                        <ListItemIcon aria-hidden="true">
                                                            {(uniqueTimeKeepers.length > 1) ? (
                                                                <Badge
                                                                    badgeContent={uniqueTimeKeepers.length}
                                                                    classes={{ badge: classes.customBadge }}
                                                                >
                                                                    <Styled.SmallAvatar primary={true}>
                                                                        {getInitials(currentTimekeeper.name)}
                                                                    </Styled.SmallAvatar>
                                                                </Badge>
                                                            ) : (
                                                                <Styled.SmallAvatar primary={true}>
                                                                    {getInitials(currentTimekeeper.name)}
                                                                </Styled.SmallAvatar>
                                                            )}
                                                        </ListItemIcon>
                                                        {this.state.open && <Styled.NavItemText primary={currentTimekeeper.name}/>}
                                                    </Styled.NavItem>
                                                </div>
                                            )}
                                            collaborate={false}
                                            timeKeepers={filteredTimeKeepers}
                                            menuWidth={300}
                                            workDate={DateTime.local().startOf('day').toISO()}
                                        />
                                        {this.customListItem(this.props.appStore!.user.displayName, undefined, undefined,
                                            <Styled.NavItem
                                                onClick={() => this.setState({userMenuOpen: true})}
                                                button={true}
                                                buttonRef={this.userMenuAnchor}
                                            >
                                                <ListItemIcon aria-hidden="true">
                                                    <Styled.SmallAvatar>{getInitials(user.displayName)}</Styled.SmallAvatar>
                                                </ListItemIcon>
                                                <Styled.NavItemText primary={user.displayName}/>
                                            </Styled.NavItem>
                                        )}

                                        <Menu
                                            anchorOrigin={{horizontal: 'center', vertical: 'center'}}
                                            anchorEl={this.userMenuAnchor.current}
                                            open={this.state.userMenuOpen}
                                            onClose={() => this.setState({userMenuOpen: false})}
                                        >
                                            <MenuItem
                                                disabled={!online}
                                                onClick={this.logout}
                                            >
                                                {t('app.nav_bar.user.menu.logout.label')}
                                            </MenuItem>
                                        </Menu>
                                        {this.state.open ? (
                                            <Styled.NavItem
                                                button={true}
                                                onClick={this.handleDrawerClose}
                                            >
                                                <ListItemIcon><ChevronLeft className={classes.icon} /></ListItemIcon>
                                                <Styled.NavItemText primary={t('collapse')}/>
                                            </Styled.NavItem>
                                        ) : (
                                            <Styled.NavItem
                                                button={true}
                                                onClick={this.handleDrawerOpen}
                                                aria-label={t('expand')}
                                            >
                                                <ListItemIcon><ChevronRight className={classes.icon} /></ListItemIcon>
                                            </Styled.NavItem>
                                        )}
                                    </Styled.IconList>
                                </Drawer>
                            </>
                        }
                    </>
                }
            </FeaturesConsumer>
        );
    }
}

// tslint:disable-next-line:no-any
export default  withRouter(withStyles(NavBarStyles as any, { withTheme: true })(withTranslation(['common'])(NavBar)));